import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import type { Account, AccountContact } from '@/api/commercial/accounts';
import { type CreditAnalysis, creditAnalysisApi } from '@/api/commercial/credit-analysis';
import { DocumentUploadForm } from '@/components/forms/commercial/credit-analysis/document-upload-form';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const sendDocumentFormSchema = z
  .object({
    emails: z.string().optional(),
    contacts: z.array(z.any()).optional(),
  })
  .refine(
    (data) => {
      const hasEmails = data.emails && data.emails.trim() !== '';
      const hasContacts = data.contacts && data.contacts.length > 0;
      return hasEmails || hasContacts;
    },
    {
      message: 'Informe ao menos um email ou selecione ao menos um contato',
      path: ['emails'],
    },
  );

export type SendDocumentSchema = z.infer<typeof sendDocumentFormSchema>;

interface SendDocumentRequestFormProps {
  proposalId: string;
  accountId?: string;
  account?: Account | null;
  contacts?: AccountContact[];
  onSuccess?: (creditAnalysis: CreditAnalysis, emails?: string[]) => void;
}

interface CustomInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  help?: string;
  placeholder?: string;
}

function CustomInput({ label, value, onChange, error, help, placeholder }: CustomInputProps) {
  const inputId = `input-${label?.replace(/\s+/g, '-').toLowerCase()}-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium leading-6 text-gray-900">
          {label}
        </label>
      )}
      <div className={label ? 'mt-2' : ''}>
        <input
          id={inputId}
          type="text"
          className={cn(
            'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:pl-3 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6',
            error && 'ring-red-600 focus:ring-red-600',
          )}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {help && !error && <p className="text-xs text-gray-500 mt-1">{help}</p>}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </div>
  );
}

export function SendDocumentRequestForm({
  proposalId,
  accountId,
  account,
  contacts = [],
  onSuccess,
}: SendDocumentRequestFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<SendDocumentSchema>({
    resolver: zodResolver(sendDocumentFormSchema),
    defaultValues: {
      contacts: [],
      emails: '',
    },
  });

  const onSendEmailSubmit = async (data: SendDocumentSchema) => {
    setIsSendingEmail(true);
    try {
      // Parse emails from string
      const emailList = [
        ...(data.emails || '')
          .split(/[,;]+/)
          .map((email) => email.trim())
          .filter((email) => email !== ''),

        ...(data.contacts ?? []).flatMap(
          (item) =>
            (item as AccountContact).contactItems
              ?.filter((ci) => ci.type === 'Email')
              .map((ci) => ci.contact) ?? [],
        ),
      ].filter((email, index, self) => email && self.indexOf(email) === index);

      // Use the credit analysis API to send the document request
      const creditAnalysis = await creditAnalysisApi.sendDocumentRequest({
        proposalId,
        accountId,
        emails: emailList,
      });

      toast({
        title: 'Email enviado',
        description: 'O email foi enviado com sucesso.',
      });

      // Invalidate queries to refetch credit analysis
      queryClient.invalidateQueries({
        queryKey: ['creditAnalysis'],
      });
      queryClient.invalidateQueries({
        queryKey: ['creditAnalysis', 'proposal', proposalId],
      });
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: ['creditAnalysis', 'account', accountId],
        });
      }

      // Call success callback with the created credit analysis and emails
      onSuccess?.(creditAnalysis, emailList);
    } catch (e) {
      console.error('Error sending document request:', e);
      toast({
        title: 'Erro ao enviar email',
        description: 'Tente novamente mais tarde.',
        variant: 'destructive',
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Show upload form instead of placeholder
  if (showUploadForm) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          className="gap-2"
          type="button"
          onClick={() => setShowUploadForm(false)}
        >
          Voltar
        </Button>

        <DocumentUploadForm
          proposalId={proposalId}
          account={account || null}
          accountType={account?.document?.type === 'cpf' ? 'PF' : 'ME'}
          creditAnalysis={null}
          requiredDocuments={[]}
          onSuccess={(creditAnalysis) => {
            setShowUploadForm(false);
            onSuccess?.(creditAnalysis);
          }}
          onCancel={() => setShowUploadForm(false)}
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSendEmailSubmit)} className="w-full space-y-4">
      {/* Debug: Always show this form if we reach here */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <p className="text-sm flex-1">
          Informe o(s) contato(s) de email para fazer a solicitação de envio da documentação para
          análise de crédito:
        </p>
        <Button
          type="button"
          onClick={() => setShowUploadForm(true)}
          className="shrink-0 whitespace-nowrap"
        >
          <Paperclip className="h-4 w-4 mr-2" />
          Anexar documentos
        </Button>
      </div>

      <div className="w-[50vw]">
        <label
          htmlFor="contacts-combobox"
          className="block text-sm font-medium leading-6 text-gray-900 mb-2"
        >
          Contatos
        </label>
        <Controller
          name="contacts"
          control={control}
          render={({ field }) => (
            <Combobox
              id="contacts-combobox"
              type="multiple"
              data={contacts}
              placeholder="Selecione um contato"
              value={field.value ?? []}
              keyExtractor={(item) => item.id}
              displayValueGetter={(item: AccountContact) =>
                item.contactItems?.find((i) => i.type === 'Email')?.contact ?? ''
              }
              onOptionChange={field.onChange}
            />
          )}
        />

        <Controller
          name="emails"
          control={control}
          render={({ field }) => (
            <CustomInput
              label="Outros emails"
              placeholder="example@mail.com; example2@mail.com"
              value={field.value ?? ''}
              onChange={field.onChange}
              help="Separe múltiplos emails com ponto e vírgula (;)"
              error={errors?.emails?.message}
            />
          )}
        />
      </div>

      <div className="flex w-full justify-end">
        <Button type="submit" disabled={isSendingEmail}>
          {isSendingEmail ? 'Enviando...' : 'Enviar solicitação'}
        </Button>
      </div>
    </form>
  );
}
